import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from '../../../../../config/axios'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  ContentContainer,
  Section,
  SectionHeader,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  MaintenanceTable,
  TableCell,
  TableCellValue,
  TableCellLabel,
  MaintenanceItemContainer,
  HistoryTable,
  HistoryTableCell,
  HistoryItemContainer,
  ButtonContainer,
  EditButton,
} from './style'
import { BlurBackground } from '../../style'
import cross from '../../../../../assets/addStockModal/cross.svg'
import editIcon from '../../../../../assets/edit-pen-white.svg'

const API_BASE_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/equipment`

interface ViewEquipmentModalProps {
  machineId: number
  onClose: () => void
}

interface ServiceProviderUser {
  id: number
  name: string
  email: string
  phone?: string
  company: string
  cnpj?: string
  role: string
  approved: boolean
  picture?: string
}

interface ServiceProviderCompany {
  id: number
  companyName: string
  cnpj: string
  email: string
  phone?: string
  contactName?: string
}

interface BackendPeriodicMaintenanceItem {
  id: number
  maintenanceName: string
  periodicityValue: number
  periodicityUnit: 'dias' | 'semanas' | 'meses' | 'anos'
  isVerificationEnabled: boolean
  verificationValue?: number
  verificationUnit?: 'dias' | 'semanas' | 'meses' | 'anos'
  isInternalService: boolean
  serviceProviderUserId?: number
  serviceProviderCompanyId?: number
  serviceProviderUser?: ServiceProviderUser
  serviceProviderCompany?: ServiceProviderCompany
}

interface BackendMaintenanceHistoryItem {
  id: number
  maintenanceName: string
  maintenanceDate: string
  value: number
  type: string
}

interface BackendMachineDetail {
  id: number
  code: string
  name: string
  model: string
  manufacturer: string
  year: number
  type: string
  dimension: string
  weight: number
  location: string
  costPerHour: number
  capacity: string
  selectedServiceProviders: ServiceProviderUser[]
  periodicMaintenances: BackendPeriodicMaintenanceItem[]
  maintenanceHistory?: BackendMaintenanceHistoryItem[]
}

interface MachineDetailItem {
  label: string
  value: string
}

interface PeriodicMaintenanceItem {
  name: string
  periodicity: string
  verification: string
  serviceProvider: string
  serviceType: 'Interno' | 'Externo'
}

interface MaintenanceHistoryItem {
  name: string
  date: string
  value: string
  type: string
}

interface FullMachineData {
  general: MachineDetailItem[]
  operation: MachineDetailItem[]
  maintenanceService: MachineDetailItem[]
  periodicMaintenance: PeriodicMaintenanceItem[]
  maintenanceHistory: MaintenanceHistoryItem[]
}

const ViewEquipmentModal: React.FC<ViewEquipmentModalProps> = ({
  machineId,
  onClose,
}) => {
  const navigate = useNavigate()

  const [machineData, setMachineData] = useState<FullMachineData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  const formatCurrency = useCallback(
    (value: number | string | undefined | null): string => {
      const numValue = typeof value === 'string' ? parseFloat(value) : value
      if (
        isNaN(numValue as number) ||
        numValue === null ||
        numValue === undefined
      ) {
        return 'N/A'
      }
      return numValue.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    [],
  )

  const formatDate = useCallback((dateString?: string): string => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        const [year, month, day] = dateString.split('-').map(Number)
        if (year && month && day) {
          const parsedDate = new Date(year, month - 1, day)
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('pt-BR')
          }
        }
        return dateString
      }
      return date.toLocaleDateString('pt-BR')
    } catch (e) {
      console.warn('Could not parse date string:', dateString, e)
      return dateString
    }
  }, [])

  const getServiceProviderInfo = useCallback(
    (maintenance: BackendPeriodicMaintenanceItem): { name: string; type: 'Interno' | 'Externo' } => {
      if (maintenance.isInternalService) {
        if (maintenance.serviceProviderUser) {
          return {
            name: maintenance.serviceProviderUser.name,
            type: 'Interno'
          }
        }
        return { name: 'Usuário não encontrado', type: 'Interno' }
      } else {
        if (maintenance.serviceProviderCompany) {
          return {
            name: maintenance.serviceProviderCompany.companyName,
            type: 'Externo'
          }
        }
        return { name: 'Empresa não encontrada', type: 'Externo' }
      }
    },
    [],
  )

  useEffect(() => {
    const fetchMachineData = async () => {
      if (!machineId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      const token = localStorage.getItem('token')
      if (!token) {
        setError('Sessão expirada. Faça login novamente.')
        setLoading(false)
        navigate('/login')
        return
      }

      try {

        const response = await axios.get<BackendMachineDetail>(
          `${API_BASE_URL}/equipment/${machineId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )

        const backendData = response.data
        const mappedGeneral: MachineDetailItem[] = [
          { label: 'Código', value: backendData.code || 'N/A' },
          { label: 'Nome', value: backendData.name || 'N/A' },
          { label: 'Modelo', value: backendData.model || 'N/A' },
          { label: 'Fabricante', value: backendData.manufacturer || 'N/A' },
          { label: 'Ano', value: backendData.year?.toString() || 'N/A' },
          { label: 'Tipo', value: backendData.type || 'N/A' },
          { label: 'Dimensão', value: backendData.dimension || 'N/A' },
          { label: 'Peso', value: backendData.weight ? `${backendData.weight} kg` : 'N/A' },
          { label: 'Local', value: backendData.location || 'N/A' },
        ]

        const mappedOperation: MachineDetailItem[] = [
          {
            label: 'Custo/Hora',
            value: formatCurrency(backendData.costPerHour),
          },
          { label: 'Capacidade', value: backendData.capacity || 'N/A' },
        ]

        const mappedMaintenanceService: MachineDetailItem[] = [
          {
            label: 'Prestadores de Serviço',
            value:
              backendData.selectedServiceProviders && backendData.selectedServiceProviders.length > 0
                ? backendData.selectedServiceProviders
                    .map((provider) => provider.name || 'Nome não disponível')
                    .join(', ')
                : 'Nenhum prestador cadastrado',
          },
        ]

        const mappedPeriodicMaintenance: PeriodicMaintenanceItem[] = (
          backendData.periodicMaintenances || []
        ).map((item) => {
          const serviceProviderInfo = getServiceProviderInfo(item)
          
          return {
            name: item.maintenanceName || 'N/A',
            periodicity: `A cada ${item.periodicityValue || 'N/A'} ${
              (item.periodicityUnit || '')
            }`.trim(),
            verification: item.isVerificationEnabled
              ? `A cada ${item.verificationValue || 'N/A'} ${
                  (item.verificationUnit || '')
                }`.trim()
              : 'Desabilitado',
            serviceProvider: serviceProviderInfo.name,
            serviceType: serviceProviderInfo.type
          }
        })

        const mappedMaintenanceHistory: MaintenanceHistoryItem[] = (
          backendData.maintenanceHistory || []
        ).map((item) => ({
          name: item.maintenanceName || 'N/A',
          date: formatDate(item.maintenanceDate),
          value: item.value?.toString() || 'N/A',
          type: item.type || 'N/A',
        }))

        setMachineData({
          general: mappedGeneral,
          operation: mappedOperation,
          maintenanceService: mappedMaintenanceService,
          periodicMaintenance: mappedPeriodicMaintenance,
          maintenanceHistory: mappedMaintenanceHistory,
        })
      } catch (err) {
        console.error('Erro ao buscar dados da máquina:', err)
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 401) {
            setError('Não autorizado. Por favor, faça login novamente.')
            navigate('/login')
          } else if (err.response?.status === 404) {
            setError('Máquina não encontrada.')
          } else {
            setError(
              `Erro ao carregar dados: ${err.response?.data?.message || err.message}`
            )
          }
        } else {
          setError('Falha ao carregar os dados da máquina. Por favor, tente novamente.')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchMachineData()
  }, [machineId, navigate, formatCurrency, formatDate, getServiceProviderInfo])

  const handleClose = () => {
    onClose()
  }

  const handleEditMachine = () => {
    navigate(`/panel/equipment/edit/${machineId}`)
    onClose()
  }

  if (loading) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Carregando dados da máquina...</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Carregando informações da máquina...</p>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (error) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Erro ao carregar dados</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
              <button onClick={handleClose}>Fechar</button>
            </div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  if (!machineData) {
    return (
      <BlurBackground>
        <Container>
          <HeaderDiv>
            <HeaderText>Máquina não encontrada</HeaderText>
            <CloseDiv onClick={handleClose}>
              <img src={cross} alt='Fechar' />
            </CloseDiv>
          </HeaderDiv>
          <ContentContainer>
            <div>Nenhum dado encontrado para a máquina com ID: {machineId}.</div>
          </ContentContainer>
        </Container>
      </BlurBackground>
    )
  }

  const machineCodeItem = machineData.general.find(
    (item) => item.label === 'Código',
  )
  const displayCode = machineCodeItem
    ? machineCodeItem.value
    : `MP-${String(machineId).padStart(5, '0')}`

  return (
    <BlurBackground>
      <Container>
        <HeaderDiv>
          <HeaderText>Máquina: {displayCode}</HeaderText>
          <CloseDiv onClick={handleClose}>
            <img src={cross} alt='Fechar' />
          </CloseDiv>
        </HeaderDiv>

        <ContentContainer>
          <Section>
            <SectionHeader>Dados gerais</SectionHeader>
            <DetailGrid>
              {machineData.general.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Operação</SectionHeader>
            <DetailGrid>
              {machineData.operation.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Serviço de manutenção</SectionHeader>
            <DetailGrid>
              {machineData.maintenanceService.map((item, index) => (
                <DetailItem key={index}>
                  <DetailLabel>{item.label}</DetailLabel>
                  <DetailValue>{item.value}</DetailValue>
                </DetailItem>
              ))}
            </DetailGrid>
          </Section>

          <Section>
            <SectionHeader>Manutenção periódica</SectionHeader>
            <MaintenanceTable>
              {machineData.periodicMaintenance.length > 0 ? (
                machineData.periodicMaintenance.map((item, index) => (
                  <MaintenanceItemContainer key={index}>
                    <TableCell>
                      <TableCellLabel>Nome</TableCellLabel>
                      <TableCellValue>{item.name}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Periodicidade</TableCellLabel>
                      <TableCellValue>{item.periodicity}</TableCellValue>
                    </TableCell>
                    <TableCell>
                      <TableCellLabel>Verificação</TableCellLabel>
                      <TableCellValue>{item.verification}</TableCellValue>
                    </TableCell>
                  </MaintenanceItemContainer>
                ))
              ) : (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    color: '#666',
                    padding: '2rem'
                  }}
                >
                  Nenhuma manutenção periódica registrada.
                </div>
              )}
            </MaintenanceTable>
          </Section>

          <Section>
            <SectionHeader>Histórico de manutenção</SectionHeader>
            <HistoryTable>
              {machineData.maintenanceHistory.length > 0 ? (
                machineData.maintenanceHistory.map((item, index) => (
                  <HistoryItemContainer key={index}>
                    <HistoryTableCell>
                      <TableCellLabel>Nome</TableCellLabel>
                      <TableCellValue>{item.name}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Data</TableCellLabel>
                      <TableCellValue>{item.date}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Valor</TableCellLabel>
                      <TableCellValue>{item.value}</TableCellValue>
                    </HistoryTableCell>
                    <HistoryTableCell>
                      <TableCellLabel>Tipo</TableCellLabel>
                      <TableCellValue>{item.type}</TableCellValue>
                    </HistoryTableCell>
                  </HistoryItemContainer>
                ))
              ) : (
                <div
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    color: '#666',
                    padding: '2rem'
                  }}
                >
                  Nenhum histórico de manutenção encontrado.
                </div>
              )}
            </HistoryTable>
          </Section>
        </ContentContainer>

        <ButtonContainer>
          <EditButton onClick={handleEditMachine}>
            <img src={editIcon} alt='Editar' />
            Editar máquina
          </EditButton>
        </ButtonContainer>
      </Container>
    </BlurBackground>
  )
}

export default ViewEquipmentModal
